import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { UntypedFormGroup } from '@angular/forms';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { CdkDrag, CdkDragDrop, CdkDropList, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { noop, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, filter, switchMap, tap, catchError } from 'rxjs/operators';

import { OnDestroyMixin, untilComponentDestroyed } from '@w11k/ngx-componentdestroyed';
import { Project, Pipeline, KanbanDefinition, ProjectTemplate } from 'app/modules/projects/projects.types';
import { AccountService, AuthService } from 'app/core';
import {
  FieldDef,
  PropertyType,
  Account,
  ProjectStage,
  MarketStatus,
  ProjectType,
} from 'app/core/models/account.types';
import { ProjectsFilterComponent } from 'app/modules/projects/modals/projects-filter/projects-filter.component';
import { ProjectsFilterSaveComponent } from 'app/modules/projects/modals/projects-filter-save/projects-filter-save.component';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { ProjectAddComponent } from 'app/modules/projects/modals/project-add/project-add.component';
import { ProjectsListComponent } from 'app/modules/projects/list/list.component';
import { ProjectEditPipelineComponent } from '../../modals/project-edit-pipeline/project-edit-pipeline.component';
import { ProjectChangeStatusComponent } from 'app/modules/projects/modals/project-change-status/project-change-status.component';
import { LayoutService } from 'app/layout/layout.service';
import { PipelineService } from '../../pipeline.service';
import { FoldersTabService } from 'app/shared/modules/folders/folders.service';
import { Dictionary } from 'app/shared/models/dictionary';
import { FieldLayoutEditComponent } from '../../../../shared/components/field-layout-edit/field-layout-edit.component';
import { FieldLayoutOptions } from '../../../../shared/components/field-layout-edit/field-layout-edit.types';
import { RelatedToType } from 'app/shared/enums';
import UrlUtils from 'app/shared/Utils/url.utils';
import { setupAddDealByDrawer } from '../../@shared/functions.utils';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { ConfirmService } from 'app/shared/components/widgets/dialog/confirm/confirm.service';
import { UpdateProjectStatusService } from '../../@shared/services/update-project-status.service';
import { FeatureFlag, NewPermission, Permission } from 'app/core/enums';
import { User } from 'app/core/models';
import { ScrolDebouncerData, ScrollEvent, GroupingFieldsEnum } from './kanban.types';
import { PartialFormField } from 'app/shared/components/form-fields/form-field.types';
import { AccountState } from '@state/account/state';
import { Store } from '@ngxs/store';
import { UserState } from '@state/user/state';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import {
  getAllowedFields,
  getHeaderLabel,
  getLinkedFieldValue as _getLinkedFieldValue,
  isLinkedFieldColumn,
} from 'app/shared/functions';

@Component({
  selector: 'projects-list-kanban',
  templateUrl: './kanban.component.html',
  styleUrls: ['./kanban.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProjectsListKanbanComponent extends OnDestroyMixin implements OnInit, OnDestroy {
  account: Account;
  pipeline: Pipeline;
  projects: Project[];
  isLoading: boolean;
  displayColumns: string[] = [];
  loopDisplayColumns: string[] = [];
  displayColumnsEditMode: boolean;
  customFieldForm: UntypedFormGroup;
  data: any;
  groupedProjects: Dictionary<any> = {};
  uploadingFile: boolean = false;
  pipelines: Pipeline[];
  propertyTypes: PropertyType[];
  fieldDefs: FieldDef[] = [];
  queryFilters: any;
  savedFilter: any = null;
  defaultKanbanDefinition = [
    ['address_string', 'unit_size', 'updated_at'],
    ['broker_guidance_price', 'going_in_noi', 'rentable_square_feet', 'pipeline_id'],
  ];
  maxColumnCount = 0;
  columnsAndRows = [];
  defaultGroupBy = GroupingFieldsEnum.ProjectStage;
  lanes: any[] = [];
  groupByOptions: { [key: string]: string } = {
    assigned_user: 'Assigned User',
    investment_type: 'Investment Type',
    project_phase: 'Phase',
    project_stage: 'Status',
    property_type: 'Property Type',
    team: 'Team',
    vehicle: 'Vehicle',
  };
  isLoadingLaneKeys: string[] = [];
  lanePagination: Dictionary<number> = {};
  groupBy = this.defaultGroupBy;
  lanesCount: Dictionary<number> = {};
  Permission = Permission;
  NewPermission = NewPermission;
  getHeaderLabel = getHeaderLabel;
  isLinkedFieldColumn = isLinkedFieldColumn;
  groupLoaderData = [];

  @ViewChildren('lane') laneElements: QueryList<any>;

  private groupByQueryMap: { [key: string]: string } = {
    assigned_user: 'assigned_user_id',
    investment_type: 'investment_type_id',
    project_phase: 'project_phase_id',
    project_stage: 'project_stage_id',
    property_type: 'property_type_id',
    team: 'team_id',
    vehicle: 'vehicle_id',
    pipeline_id: 'pipeline_id',
  };
  private scrollDebouncer = new Subject<ScrolDebouncerData>();
  private pageSize = 20;

  get options(): Dictionary<any> {
    return this.data.options;
  }

  get kanbanDefinition(): KanbanDefinition {
    return this.pipeline.kanban_definition as KanbanDefinition;
  }

  get kanbanFields(): string[][] {
    return this.kanbanDefinition?.fields || this.defaultKanbanDefinition;
  }

  get canAddDeal(): boolean {
    return this.authService.hasPermission(Permission.DEALS_ADD);
  }

  get isDynamicPipeline(): boolean {
    return this.pipeline.pipeline_type === 'dynamic';
  }

  private get projectTemplates(): { id: number; name: string }[] {
    return this.options.project_templates
      .filter(({ hidden, related_to_type }) => !hidden && related_to_type === RelatedToType.Project)
      .sort((a, b) => a.rank - b.rank);
  }

  private get selectTypeFieldDefs(): FieldDef[] {
    return this.fieldDefs.filter((field) => field.type === 'select' && !field.meta.multi_select);
  }

  get isGroupCustom(): boolean {
    return this.selectTypeFieldDefs.find(({ name }) => name === this.groupBy)?.meta?.is_custom;
  }

  private get groupHasOptions(): boolean {
    return this.selectTypeFieldDefs.find(({ name }) => name === this.groupBy)?.meta?.options?.length > 0;
  }

  private get zoomLevel(): number {
    return Math.round(window.devicePixelRatio * 100);
  }

  constructor(
    public projectsListComponent: ProjectsListComponent,
    private readonly activatedRoute: ActivatedRoute,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly accountService: AccountService,
    private readonly projectsService: ProjectsService,
    private readonly router: Router,
    private readonly matDialog: MatDialog,
    private readonly snackBar: MatSnackBar,
    private readonly layoutService: LayoutService,
    private readonly pipelineService: PipelineService,
    private readonly foldersTabService: FoldersTabService,
    private readonly confirmService: ConfirmService,
    private readonly updateProjectStatus: UpdateProjectStatusService,
    private readonly authService: AuthService,
    private readonly store: Store,
  ) {
    super();

    this.accountService.currentAccount.pipe(untilComponentDestroyed(this)).subscribe((accountData) => {
      this.account = accountData;
    });

    this.displayColumns = [
      'id',
      'title',
      'project_stage_id',
      'city',
      'postal_code',
      'rentable_square_feet',
      'unit_size',
      'num_buildings',
      'street_address',
      'state',
      'updated_at',
      'broker_guidance_price',
      'going_in_noi',
      'bid_price',
      'noi',
      'occupancy',
      'address_string',
      'num_open_tasks',
      'num_tasks',
    ];
  }
  readonly hasNestedFiltersOn = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS));
  private getNestedOptions(): any {
    return this.hasNestedFiltersOn ? { fieldDefs: this.fieldDefs } : null;
  }
  ngOnInit(): void {
    this.pipeline = this.activatedRoute.snapshot.data.pipeline;

    this.activatedRoute.data.pipe(untilComponentDestroyed(this)).subscribe((data: Data) => {
      this.data = data;
      this.pipeline = data.pipeline;
      UrlUtils.saveToLocalStorage('projectPipeline', this.pipeline.id);
      if (this.hasNestedFiltersOn) {
        this.fieldDefs = this.store.selectSnapshot(
          ObjectsManagerState.getFieldsByObjectName(RelatedToType.Project, {
            allowedFieldsOnly: true,
            addNestedFields: true,
          }),
        );
      } else {
        this.fieldDefs = getAllowedFields(data.fieldDefs, RelatedToType.Project);
      }
      this.propertyTypes = this.data.options.property_types.filter((option) => !option.hidden);
      this.setGroupByOptions();
    });

    this.setupKanbanFields();
    this.loadProjects();
    this.lanes = this.getValidLanes();

    this.activatedRoute.queryParams
      .pipe(
        untilComponentDestroyed(this),
        switchMap((params) => {
          this.isLoading = true;
          const filterId = params['filterId'];

          if (filterId && filterId !== 'reset') {
            return this.accountService.getSavedFilterById(parseInt(filterId)).pipe(
              switchMap((savedFilter) => {
                this.savedFilter = savedFilter;
                this.queryFilters = JSON.parse(savedFilter.filters);
                this.saveQueryFilters(this.queryFilters);
                return this.getKanban();
              }),
            );
          } else {
            this.resetQueryFilters();

            return this.projectsService.pipeline$.pipe(
              untilComponentDestroyed(this),
              switchMap((pipeline) => {
                if (!this.canManagePipeline()) {
                  return of(pipeline);
                }

                return !pipeline.kanban_definition?.groupBy
                  ? this.updatePipelineGroupBy(pipeline, this.defaultGroupBy)
                  : of(pipeline);
              }),
              switchMap((pipeline: Pipeline) => {
                this.pipeline = pipeline;
                this.groupBy = this.pipeline.kanban_definition?.groupBy ?? this.defaultGroupBy;
                this.isLoading = true;
                this.changeDetectorRef.detectChanges();

                if (this.isDynamicPipeline && this.pipeline.saved_filter_id) {
                  return this.accountService.getSavedFilterById(this.pipeline.saved_filter_id).pipe(
                    tap((savedFilter) => {
                      this.savedFilter = savedFilter;
                      this.queryFilters = JSON.parse(savedFilter.filters);
                      this.saveQueryFilters(this.queryFilters);
                    }),
                    switchMap(() => of(pipeline)),
                  );
                }

                return of(pipeline);
              }),
              switchMap(() => this.getKanban()),
            );
          }
        }),
      )
      .subscribe(() => {
        this.isLoading = false;
        this.changeDetectorRef.detectChanges();
      });

    UrlUtils.saveToLocalStorage('projectView', 'kanban');

    this.setupScrollDebouncer(this.scrollDebouncer, 250);

    this.projectsService.pipelines$
      .pipe(
        untilComponentDestroyed(this),
        tap((pipelines) => (this.pipelines = pipelines ?? this.pipelines)),
      )
      .subscribe(noop);
  }

  public hideSaveButton(): boolean {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return !this.store.selectSnapshot(UserState.hasPermission(NewPermission.DEALS_PUBLIC_CUSTOM_VIEWS));
    }

    return !this.store.selectSnapshot(UserState.hasPermission(Permission.PIPELINE_EDIT_DYNAMIC_FILTER));
  }

  loadProjects(): void {
    this.projectsService.projects$.pipe(untilComponentDestroyed(this)).subscribe((projects: Project[]) => {
      if (projects) {
        this.projects = projects;

        this.groupProjects();
        this.changeDetectorRef.markForCheck();
      }
    });
  }

  groupProjects(): void {
    this.groupedProjects = {};
    this.lanes = this.getValidLanes();
    let categorizedIds = [];

    for (let lane of this.lanes) {
      const results = this.projects.filter((project) => {
        if (this.isGroupCustom || this.groupHasOptions) {
          return project[this.groupBy] == lane.name;
        }

        let result = project[this.groupBy]?.id == lane.id;

        if (!result) {
          result = project[this.groupBy] == lane.id;
        }

        if (!result) {
          return project[`${this.groupBy}_id`] == lane.id;
        }

        return result;
      });

      this.groupedProjects[this.getGroupUniqueKey(lane)] = results;
      categorizedIds = [...categorizedIds, ...results.map((x) => x.id)];
    }

    const otherLane = this.lanes.find((x) => x.id === 0);
    this.groupedProjects[this.getGroupUniqueKey(otherLane)] = this.projects.filter(
      (x) => !categorizedIds.includes(x.id),
    );

    this.lanePagination = Object.keys(this.groupedProjects).reduce(
      (prev, curr) => ({ ...prev, [curr]: this.lanePagination[curr] > 1 ? this.lanePagination[curr] : 1 }),
      {},
    );
  }

  onUpdate(project: PartialFormField): Observable<boolean | Project> {
    const projectToUpdate = this.projects.find((proj) => proj.id == (project.id as unknown));

    if (this.groupBy === GroupingFieldsEnum.ProjectStage) {
      return this.updateProjectStatus.update(this.data, projectToUpdate, project).pipe(untilComponentDestroyed(this));
    } else {
      return this.projectsService.saveProject(this.getUpdatePayload(project), projectToUpdate, false, true).pipe(
        untilComponentDestroyed(this),
        catchError((error) => {
          console.error(error);
          return of(false);
        }),
      );
    }
  }

  createProject(): void {
    this.matDialog.open(ProjectAddComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: this.data,
    });
  }

  canDrop(group): (drag: CdkDrag, drop: CdkDropList) => boolean {
    if (group.hidden) {
      return () => false;
    }
    return (drag: CdkDrag, drop: CdkDropList): boolean => group.id > 0;
  }

  onProjectFileChanged(event) {
    let file = event.target.files[0];
    this.uploadingFile = true;

    this.projectsService
      .uploadOm(file)
      .pipe(untilComponentDestroyed(this))
      .subscribe((document) => {
        this.uploadingFile = false;
        this.router.navigate(['/projects', this.pipeline.id, 'pdf'], {
          queryParams: { pdf_url: encodeURIComponent(document.pdf_url), document_id: document.id },
        });
      });
  }

  navigateToProjectsAddMap(): void {
    this.router.navigate(['/projects', this.pipeline.id, 'map', 'add']);
  }

  saveQueryFilters(filters: any): void {
    UrlUtils.saveToLocalStorage('Project_queryFilters', filters, true);
  }

  fillQueryFilters(): void {
    this.queryFilters = UrlUtils.getFromLocalStorage('Project_queryFilters', true);
  }

  openFilterDialog(): void {
    const dialogRef = this.matDialog.open(ProjectsFilterComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { data: this.data, filters: this.queryFilters, savedFilter: this.savedFilter },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        switchMap((result) => {
          if (result && result.filters) {
            result.filters.projectStageIdIn =
              result.filters.projectStageIdIn.length > 0 ? result.filters.projectStageIdIn : '';
            result.filters.propertyTypeIdIn =
              result.filters.propertyTypeIdIn.length > 0 ? result.filters.propertyTypeIdIn : '';
            this.queryFilters = result.filters;
            this.saveQueryFilters(this.queryFilters);
            this.isLoading = true;
            return this.getKanban();
          } else {
            let url = this.router.url.split('?')[0];
            return this.router.navigate([url], { queryParams: { filterId: 'reset' } });
          }
        }),
      )
      .subscribe(() => (this.isLoading = false));
  }

  openFilterSaveDialog(): void {
    this.matDialog.open(ProjectsFilterSaveComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { data: this.data, filters: this.queryFilters, savedFilter: this.savedFilter },
    });
  }

  dropped(event: CdkDragDrop<any[]>): void {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
    } else {
      if (this.pipeline.pipeline_type === 'dynamic' && this.groupBy === GroupingFieldsEnum.ProjectStage) {
        const stageId = event.container.element.nativeElement.id;
        const stageInfo = this.data.options.project_stages.find((x) => x.id == stageId);
        if (!stageInfo.pipeline_ids.includes(+event.item.data.pipeline_id)) {
          this.snackBar.open('The deal is not allowed to be moved to that status.', '', { duration: 3000 });
          return;
        }
      }

      transferArrayItem(event.previousContainer.data, event.container.data, event.previousIndex, event.currentIndex);

      let payload = {
        id: event.item.data.id,
      };

      /**
       * LeaseType and TenancyType are string columns, not a relationship, meaning there is no lease_type_id and tenancy_type_id
       * as it is being rendered as lease_type and tenancy_type.
       */
      if (
        this.isGroupCustom ||
        this.groupBy === GroupingFieldsEnum.LeaseType ||
        this.groupBy === GroupingFieldsEnum.TenancyType
      ) {
        payload[this.groupBy] = this.lanes.find((lane) => lane.id == event.container.element.nativeElement.id).name;
      } else if (this.groupBy.includes('_id')) {
        payload[this.groupBy] = event.container.element.nativeElement.id;
      } else {
        const id = event.container.element.nativeElement.id;
        const value = this.lanes.find((x) => x.id == id)?.value;

        if (value) {
          payload[this.groupBy] = value;
        } else {
          payload[`${this.groupBy}_id`] = event.container.element.nativeElement.id;
        }
      }

      this.onUpdate(payload)
        .pipe(untilComponentDestroyed(this))
        .subscribe((updatedWithSuccess) => {
          if (!updatedWithSuccess) {
            transferArrayItem(
              event.container.data,
              event.previousContainer.data,
              event.currentIndex,
              event.previousIndex,
            );
            this.changeDetectorRef.markForCheck();
          }
        });
    }

    this.changeDetectorRef.markForCheck();
  }

  collapse(groupUniqueKey: string, status: boolean): void {
    const name = groupUniqueKey.substring(0, groupUniqueKey.lastIndexOf('-'));
    this.lanes = this.lanes.map((x) => ({ ...x, collapse: x.name === name ? status : x.collapse }));
    this.changeDetectorRef.markForCheck();
  }

  deleteProject(project: Project): void {
    const projectIsParent = project.is_parent === 'true';
    this.confirmService
      .openConfirmationModal({
        message: `Are you sure you would like to delete ${project.title}?${
          projectIsParent ? '\nThis will delete all related child deals as well.' : ''
        }`,
      })
      .pipe(filter((result: boolean) => result))
      .subscribe(() => {
        const id = project.id;
        this.projectsService
          .deleteProject(id)
          .pipe(untilComponentDestroyed(this))
          .subscribe(() => {
            this.changeDetectorRef.markForCheck();
          });
      });
  }

  moveDeal(project: Project): void {
    const dialogRef = this.matDialog.open(ProjectEditPipelineComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { project: project, pipelines: this.pipelines, currentPipeline: this.pipeline, data: this.data },
    });

    dialogRef
      .afterClosed()
      .pipe(untilComponentDestroyed(this))
      .subscribe((result) => {
        if (result && result.moved) {
          this.projectsService.updateProjectSubjectValue(this.projects.filter(({ id }) => result.project.id !== id));
          this.snackBar.open('Deal has been moved.', '', {
            duration: 3000,
          });
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  changeStatus(project: Project): void {
    const dialogRef = this.matDialog.open(ProjectChangeStatusComponent, {
      panelClass: 'mailbox-compose-dialog',
      data: { projects: [project], pipelines: this.pipelines, data: this.data, pipeline: this.pipeline },
    });

    dialogRef
      .afterClosed()
      .pipe(
        untilComponentDestroyed(this),
        switchMap((result) => {
          if (result && result.updated) {
            this.snackBar.open('Deal status has been updated.', '', {
              duration: 3000,
            });

            return this.getKanban();
          }

          return of(null);
        }),
      )

      .subscribe(() => this.changeDetectorRef.markForCheck());
  }

  openProjectDialog(project: Project): void {
    this.router.navigate(['../' + project.id + '/details/overview'], { relativeTo: this.activatedRoute });
  }

  filterProjects(filter: any): void {
    this.isLoading = true;
    this.queryFilters = filter;
    this.lanePagination = {};

    this.getKanban()
      .pipe(untilComponentDestroyed(this))
      .subscribe(() => (this.isLoading = false));
  }

  resetQueryFilters(): void {
    this.queryFilters = {};
    this.saveQueryFilters(this.queryFilters);
    this.savedFilter = null;
    UrlUtils.saveToLocalStorage('savedFilter', '', false);
  }

  trackByFn(index: number, item: any): any {
    return item?.id || index;
  }

  setGroupByOptions(): void {
    const groupableFieldDefs = this.selectTypeFieldDefs;

    for (let fieldDef of groupableFieldDefs) {
      if (!Object.values(this.groupByQueryMap).includes(fieldDef.name)) {
        this.groupByOptions[fieldDef.name] = fieldDef.label;
      }
    }
  }

  openCustomDrawer(): void {
    setupAddDealByDrawer(
      {
        projectService: this.projectsService,
        pipelineService: this.pipelineService,
        foldersTabService: this.foldersTabService,
        layoutService: this.layoutService,
        router: this.router,
        matDialog: this.matDialog,
        authService: this.authService,
      },
      {
        pipeline: this.pipeline,
        account: this.account,
        fieldDefs: this.fieldDefs,
        data: this.data,
        projectTemplates: this.projectTemplates,
        createPortfolio: true,
      },
    ).subscribe(noop);
  }

  editLayout(): void {
    of(this.pipeline)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(({ kanban_definition }) => {
          return kanban_definition
            ? of(this.pipeline)
            : this.updateKanbanDefinition({ fields: this.defaultKanbanDefinition });
        }),
        tap((pipeline: Pipeline) => {
          this.pipeline = pipeline;

          this.layoutService.openComponentDrawer<FieldLayoutOptions>(FieldLayoutEditComponent, {
            inputs: {
              ui: {
                title: 'Edit kanban layout',
              },
              data: { fieldDefs: this.fieldDefs },
              fields: this.kanbanFields,
              filterFieldsBy: RelatedToType.Project,
              hasNestedFiltersOn: this.hasNestedFiltersOn,
            },
            outputs: {
              onCancel: () => this.layoutService.closeComponentDrawer(),
              onSave: (fields: string[][]) => {
                this.isLoading = true;
                this.updateKanbanDefinition({ fields })
                  .pipe(
                    untilComponentDestroyed(this),
                    tap((updatedPipeline: Pipeline) => {
                      this.pipeline = updatedPipeline;
                      this.setupKanbanFields();
                      this.layoutService.closeComponentDrawer();
                    }),
                    switchMap(() => this.getKanban()),
                    tap(() => {
                      this.loadProjects();
                      this.isLoading = false;
                    }),
                  )
                  .subscribe(noop);
              },
            },
          });
        }),
      )
      .subscribe(noop);
  }

  lookupField(name: string): FieldDef {
    return this.fieldDefs.find((fieldDef) => fieldDef.name == name);
  }

  canManagePipeline(): boolean {
    return this.authService.hasPermission(Permission.PIPELINE_MANAGE);
  }

  onChangeGroupBy(event: MatSelectChange): void {
    this.isLoading = true;
    this.groupBy = event.value;

    this.pipeline.kanban_definition.groupBy = this.groupBy;
    this.updatePipelineGroupBy(this.pipeline, this.groupBy)
      .pipe(
        untilComponentDestroyed(this),
        switchMap(() => this.getKanban()),
      )
      .subscribe(() => (this.isLoading = false));
  }

  getGroupUniqueKey({ name, id }: { name: string; id: number }): string {
    return `${name}-${id}`;
  }

  onScroll(event: ScrollEvent, group: any, groupUniqueKey: string): void {
    if (this.isLoadingLaneKeys.includes(groupUniqueKey)) {
      return;
    }

    let scrollValue = event.target.offsetHeight + event.target.scrollTop;

    if (this.zoomLevel < 100) {
      scrollValue = Math.floor(scrollValue);
    } else {
      scrollValue = Math.ceil(scrollValue);
    }

    if (scrollValue + 100 >= event.target.scrollHeight && !this.groupLoaderData.includes(groupUniqueKey)) {
      this.scrollDebouncer.next({ groupUniqueKey, group, htmlElement: event.target });
    }
  }

  onWheel(event: WheelEvent, groupUniqueKey: string) {
    if (this.isLoadingLaneKeys.includes(groupUniqueKey)) {
      event.preventDefault();
    }
  }

  canDeleteDeal(deal: Project): any {
    if (this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))) {
      return [NewPermission.DEALS_DELETE, deal?.permissions?.is_deletable];
    }

    return Permission.DEALS_DELETE;
  }

  private getUsersValues(): User[] {
    return this.options['users'].reduce((p: User[], c: User) => {
      const item = p.find((x) => x.id === c.id);
      return !item ? [...p, c] : p;
    }, []) as User[];
  }

  private getValidLanes(): any[] {
    const optionKey = `${this.groupBy}s`;

    let source = this.options[optionKey];

    switch (this.groupBy) {
      case GroupingFieldsEnum.AssignedUser:
      case GroupingFieldsEnum.CreatedBy:
      case GroupingFieldsEnum.DealLead:
      case GroupingFieldsEnum.DevManager:
      case GroupingFieldsEnum.DevAnalyst:
      case GroupingFieldsEnum.ExecutionLead:
        source = this.getUsersValues();
        break;

      case GroupingFieldsEnum.ProjectStage:
        source = source
          .filter((i: Data) => {
            if (this.pipeline.pipeline_type === 'dynamic') {
              return true;
            }

            return i.pipeline_ids.includes(this.pipeline.id);
          })
          .sort((a: ProjectStage, b: ProjectStage) => a.rank - b.rank) as ProjectStage[];
        break;

      case GroupingFieldsEnum.MarketStatus:
        source = mapSourceOptions(this.options['market_statuses']) as MarketStatus[];
        break;

      case GroupingFieldsEnum.TemplateId:
        source = mapSourceOptions(this.options['project_templates']) as ProjectTemplate<any>[];
        break;

      case GroupingFieldsEnum.ProjectType:
        source = mapSourceOptions(this.options['project_types']) as ProjectType[];
        break;
    }

    function mapSourceOptions(options: any[]): any[] {
      return options.map((option) => ({
        id: option.id,
        name: option.name,
        collapse: false,
        ...option,
      }));
    }

    if (source) {
      return [
        ...source.map((x) => ({ ...x, collapse: false, hidden: x.hidden })),
        { id: 0, name: 'Others', collapse: false },
      ];
    } else {
      return this.getLanesFromSelectFields();
    }
  }

  /**
   * Generate valid lanes for custom field values.
   */
  private getLanesFromSelectFields(): any[] {
    let lanesFromFieldDefs = this.selectTypeFieldDefs.find((field) => field.name === this.groupBy)?.meta?.options;

    lanesFromFieldDefs = lanesFromFieldDefs?.map((option: unknown, index: number) => ({
      id: index + 1,
      name: option,
      value: option,
      collapse: false,
    })) as Array<any>;

    return [...lanesFromFieldDefs, { id: 0, name: 'Others', collapse: false }];
  }

  private setupScrollDebouncer<T extends ScrolDebouncerData>(subject: Subject<T>, time = 1000): void {
    subject
      .asObservable()
      .pipe(
        untilComponentDestroyed(this),
        debounceTime(time),
        switchMap(({ groupUniqueKey, group, htmlElement }) => {
          const scrollStateMemento = Number(htmlElement.scrollTop);

          this.isLoadingLaneKeys = [...this.isLoadingLaneKeys, groupUniqueKey];
          this.changeDetectorRef.detectChanges();

          htmlElement.scrollTop = htmlElement.scrollHeight;

          this.lanePagination = { ...this.lanePagination, [groupUniqueKey]: this.lanePagination[groupUniqueKey] + 1 };

          const fjson = [
            ...JSON.parse(this.queryFilters.fjson || '[]'),
            [this.groupByQueryMap[this.groupBy] ?? this.groupBy, 'in', [group.id === 0 ? null : group.id]],
          ];

          if (!this.isDynamicPipeline) {
            fjson.push(['pipeline_id', 'in', [this.pipeline.id]]);
          }

          return this.projectsService
            .getMoreForKanban(
              {
                connection: RelatedToType.Project,
                processNodeFields: true,
                fields: this.displayColumns.includes(this.groupBy)
                  ? this.displayColumns
                  : [...this.displayColumns, this.groupBy],
                filter: {
                  fjson: JSON.stringify(fjson),
                  query: this.queryFilters.q,
                  pagination: { page: this.lanePagination[groupUniqueKey], size: this.pageSize },
                  sort: [{ field: 'updated_at', order: 'desc' }],
                  filterDictionary: this.getLegacyFilterParts(),
                },
              },
              this.getNestedOptions(),
            )
            .pipe(
              tap((projects: Project[]) => {
                this.isLoadingLaneKeys = this.isLoadingLaneKeys.filter((key) => key !== groupUniqueKey);

                if (!projects?.length) {
                  this.lanePagination = {
                    ...this.lanePagination,
                    [groupUniqueKey]: this.lanePagination[groupUniqueKey] - 1,
                  };
                  if (!this.groupLoaderData.includes(groupUniqueKey)) {
                    this.groupLoaderData = [...this.groupLoaderData, groupUniqueKey];
                  }

                  this.changeDetectorRef.detectChanges();

                  const laneElement = this.laneElements.find(
                    ({ nativeElement }: any) => nativeElement.id === groupUniqueKey,
                  );

                  //Keep the scroll on the end
                  laneElement.nativeElement.scrollTop = scrollStateMemento;
                } else {
                  this.changeDetectorRef.detectChanges();

                  const laneElement = this.laneElements.find(
                    ({ nativeElement }: any) => nativeElement.id === groupUniqueKey,
                  );

                  laneElement.nativeElement.scrollTop = scrollStateMemento - 101;
                }
              }),
            );
        }),
      )
      .subscribe(noop);
  }

  private updateKanbanDefinition(model: KanbanDefinition): Observable<Pipeline> {
    return this.pipelineService.update(
      { ...this.pipeline, kanban_definition: JSON.stringify(model) },
      Number(this.account.id),
    );
  }

  private appendMissingFields(): void {
    if (this.kanbanFields) {
      const flattenedFields = this.kanbanFields.reduce((prev: string[], cur: string[]) => {
        return prev.concat(cur);
      }, []);

      flattenedFields.forEach((field) =>
        this.displayColumns.includes(field) ? undefined : this.displayColumns.push(field),
      );
    }
  }

  private setFieldsColumnsAndRows(): void {
    this.maxColumnCount = this.kanbanFields.reduce((prev: number, cur: string[]) => {
      return cur.length > prev ? cur.length : prev;
    }, 0);
    this.columnsAndRows = [];

    for (let i = 0; i < this.maxColumnCount; i++) {
      const columnArray = [];
      for (let j = 0; j < this.kanbanFields.length; j++) {
        columnArray.push(this.kanbanFields[j][i]);
      }
      this.columnsAndRows.push(columnArray);
    }
  }

  private setupKanbanFields(): void {
    this.appendMissingFields();
    this.setFieldsColumnsAndRows();
  }

  private updatePipelineGroupBy(pipeline: Pipeline, groupBy: string): Observable<Pipeline> {
    return this.pipelineService.update(
      {
        ...pipeline,
        kanban_definition: JSON.stringify({ ...(pipeline.kanban_definition || {}), groupBy }),
      },
      Number(this.account.id),
    );
  }

  /**
   * [SC-24646] Include groupBy value to displayColumns if its not already present.
   * Without this change, user would have to manually add the field they're grouping by to the 'Edit Layout' config definition drawer in UI.
   */
  private getKanban(): Observable<Project[]> {
    return this.projectsService
      .getKanban(
        {
          isDynamicView: this.isDynamicPipeline,
          fields: this.displayColumns.includes(this.groupBy)
            ? this.displayColumns
            : [...this.displayColumns, this.groupBy],
          per_page: this.pageSize,
          group: this.groupBy,
          viewId: this.pipeline.id,
          fjson: this.queryFilters.fjson ?? '[]',
          q: this.queryFilters.q ?? '',
          filterDictionary: this.getLegacyFilterParts(),
        },
        this.getNestedOptions(),
      )
      .pipe(
        tap(({ lanesCount, deals }) => {
          if (deals?.length) {
            this.lanesCount = { ...lanesCount, 0: lanesCount[''] ?? 0 };
          }
        }),
        map(({ deals }) => deals),
      );
  }

  private getLegacyFilterParts(): Dictionary<any> {
    if (this.savedFilter?.filters) {
      const filters = JSON.parse(this.savedFilter.filters);

      return Object.keys(filters)
        .filter((key) => !['fjson', 'q'].includes(key))
        .reduce((p, c) => ({ ...p, [c]: Array.isArray(filters[c]) ? filters[c].join(',') : filters[c] }), {});
    }
    return {};
  }

  getLinkedFieldValue(object: any, fieldName: string): { label: string; [key: string]: any }[] {
    return _getLinkedFieldValue(object, fieldName, {
      fieldDefs: this.fieldDefs,
      options: this.options,
    });
  }

  getLaneCount(group: any): number {
    return group.id
      ? this.lanesCount[group.id]
        ? this.lanesCount[group.id]
        : this.lanesCount[group.name]
      : group.id === 0
        ? this.lanesCount['']
        : this.groupedProjects[group.name + '-0']?.length;
  }

  private getUpdatePayload(project: any): any {
    const field = this.groupByQueryMap[this.groupBy];

    if (!field) {
      return project;
    }

    const options = [
      GroupingFieldsEnum.AssignedUser,
      GroupingFieldsEnum.CreatedBy,
      GroupingFieldsEnum.DealLead,
      GroupingFieldsEnum.DevManager,
      GroupingFieldsEnum.DevAnalyst,
      GroupingFieldsEnum.ExecutionLead,
    ].includes(this.groupBy)
      ? this.getUsersValues()
      : this.data.options[`${this.groupBy}s`];

    const option = options.find((x) => x.id == project[field]);
    const payload = { ...project, [this.groupBy]: option };

    return payload;
  }
}
